import { useEffect, useTransition } from "react";

import { SteppedMultiColumnDropdown } from "@stacklet/ui";
import { graphql, usePaginationFragment } from "react-relay";
import { useDebouncedCallback } from "use-debounce";

import { useSteppedMultiColumnDropdown } from "../../hooks/useSteppedMultiColumnDropdown";
import { FilterCriteriaCard } from "../FilterCriteriaCard";
import {
  createPathAwareDropdown,
  pathOperators,
  column,
  OPERATORS,
  VALUES,
  conditions,
} from "./DefinitionBuilder";
import { useProviderFilterCombiner } from "./hooks";

import type { AccountCriteria_accountGroups$key } from "./__generated__/AccountCriteria_accountGroups.graphql";
import type { AccountCriteria_accounts$key } from "./__generated__/AccountCriteria_accounts.graphql";
import type { AccountCriteria_tagKeys$key } from "./__generated__/AccountCriteria_tagKeys.graphql";
import type { AccountCriteria_tagValues$key } from "./__generated__/AccountCriteria_tagValues.graphql";
import type { CriteriaFilterValue } from "@stacklet/ui";

interface Props {
  refs: {
    accountsRef: AccountCriteria_accounts$key;
    accountGroupsRef: AccountCriteria_accountGroups$key;
    tagKeysRef: AccountCriteria_tagKeys$key;
    tagValuesRef: AccountCriteria_tagValues$key;
  };
  handleSelectionsChange?: (selections: CriteriaFilterValue[]) => void;
  initialCriteriaFilters?: CriteriaFilterValue[];
}

const DEFAULT_SELECTIONS = ["account-name", "is-one-of"];

export function AccountCriteria({
  refs,
  handleSelectionsChange,
  initialCriteriaFilters,
}: Props) {
  const [, startTransition] = useTransition();

  const { criteriaFilters, handleChange, handleRemove, initialSelections } =
    useSteppedMultiColumnDropdown({
      handleSelectionsChange,
      initialCriteriaFilters,
      defaultSelections: DEFAULT_SELECTIONS,
    });

  const { combineFilters } = useProviderFilterCombiner({
    fieldName: "provider",
    operator: "equals",
  });

  const {
    data: accountsData,
    loadNext: loadNextAccounts,
    hasNext: hasNextAccounts,
    refetch: refetchAccounts,
  } = usePaginationFragment(
    graphql`
      fragment AccountCriteria_accounts on Query
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 100 }
        cursor: { type: "String" }
        filter: { type: "FilterElementInput" }
      )
      @refetchable(queryName: "AccountCriteriaAccountsPaginationQuery") {
        accounts(first: $count, after: $cursor, filterElement: $filter)
          @connection(key: "AccountCriteria__accounts") {
          edges {
            node {
              key
              name
            }
          }
        }
      }
    `,
    refs.accountsRef,
  );

  const {
    data: accountGroupsData,
    loadNext: loadNextAccountGroups,
    hasNext: hasNextAccountGroups,
    refetch: refetchAccountGroups,
  } = usePaginationFragment(
    graphql`
      fragment AccountCriteria_accountGroups on Query
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 100 }
        cursor: { type: "String" }
        filter: { type: "FilterElementInput" }
      )
      @refetchable(queryName: "AccountCriteriaAccountGroupsPaginationQuery") {
        accountGroups(first: $count, after: $cursor, filterElement: $filter)
          @connection(key: "AccountCriteria__accountGroups") {
          edges {
            node {
              name
            }
          }
        }
      }
    `,
    refs.accountGroupsRef,
  );

  const {
    data: tagKeysData,
    loadNext: loadNextTagKeys,
    hasNext: hasNextTagKeys,
    refetch: refetchTagKeys,
  } = usePaginationFragment(
    graphql`
      fragment AccountCriteria_tagKeys on Query
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 100 }
        cursor: { type: "String" }
        filter: { type: "FilterElementInput" }
      )
      @refetchable(queryName: "AccountCriteriaTagKeysPaginationQuery") {
        resourceTagKeys(first: $count, after: $cursor, filterElement: $filter)
          @connection(key: "AccountCriteria__resourceTagKeys") {
          edges {
            node {
              key
            }
          }
        }
      }
    `,
    refs.tagKeysRef,
  );

  const {
    data: tagValuesData,
    loadNext: loadNextTagValues,
    hasNext: hasNextTagValues,
    refetch: refetchTagValues,
  } = usePaginationFragment(
    graphql`
      fragment AccountCriteria_tagValues on Query
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 100 }
        cursor: { type: "String" }
        filter: { type: "FilterElementInput", defaultValue: null }
      )
      @refetchable(queryName: "AccountCriteriaTagValuesPaginationQuery") {
        resourceTagValues(
          first: $count
          after: $cursor
          filterElement: $filter
        ) @connection(key: "AccountCriteria__resourceTagValues") {
          edges {
            node {
              value
            }
          }
        }
      }
    `,
    refs.tagValuesRef,
  );

  // Transform data for the dropdown (Relay automatically merges edges with @connection)
  const accountItems =
    accountsData.accounts?.edges.map((edge) => ({
      id: edge.node.key,
      name: edge.node.name,
    })) ?? [];

  const accountGroupItems =
    accountGroupsData.accountGroups?.edges.map((edge) => ({
      id: edge.node.name,
      name: edge.node.name,
    })) ?? [];

  const tagKeyItems =
    tagKeysData.resourceTagKeys?.edges.map((edge) => ({
      id: edge.node.key,
      name: edge.node.key,
    })) ?? [];

  const tagValues =
    tagValuesData.resourceTagValues?.edges.map((edge) => ({
      id: edge.node.value,
      name: edge.node.value,
    })) ?? [];

  // Refetch data when providers change
  useEffect(() => {
    startTransition(() => {
      const filter = combineFilters(null);
      refetchAccounts({ filter });
      refetchAccountGroups({ filter });
      // Tag keys are not provider-scoped; refetch without provider filter
      refetchTagKeys({});
    });
  }, [combineFilters, refetchAccounts, refetchAccountGroups, refetchTagKeys]);

  // Configuration
  const config = createPathAwareDropdown()
    .criteria([
      { id: "account-name", name: "Account Name" },
      { id: "account-tag", name: "Account Tag" },
      { id: "account-group-name", name: "Account Group" },
    ])
    // Account path
    .path("account-name", (path) =>
      path.column(pathOperators(OPERATORS.STRING_EXTENDED)).column(
        // Checkbox for multi-select operators
        column
          .checkbox("Select Account", accountItems)
          .withCondition(
            conditions.includesAny(1, "is-one-of", "is-not-one-of"),
          ),
        // Search for text-based operators
        column
          .search("Select Account", accountItems)
          .withCondition(
            conditions.includesAny(
              1,
              "equals",
              "does-not-equal",
              "contains",
              "does-not-contain",
              "starts-with",
              "ends-with",
            ),
          )
          .withPlaceholder("Search or enter value")
          .allowCustomValue(),
      ),
    )
    // Account tag path
    .path("account-tag", (path) =>
      path
        .column(
          column
            .search("Select Tag Key", tagKeyItems)
            .withPlaceholder("Search or enter tag key")
            .allowCustomValue(),
        )
        .column(
          pathOperators([...OPERATORS.STRING_EXTENDED, ...OPERATORS.BOOLEAN]),
        )
        .column(
          // Existence values shown only for the boolean "is" / "is-not" operators
          column
            .list("Select Value", VALUES.EXISTENCE)
            .withCondition(conditions.equals(2, "is", "is-not")),
          // Checkbox for multi-select operators
          column
            .checkbox("Select Tag Value", tagValues)
            .withCondition(
              conditions.includesAny(2, "is-one-of", "is-not-one-of"),
            )
            .withPlaceholder("Search"),
          // Search for single-value text operators
          column
            .search("Select value", tagValues)
            .withCondition(
              conditions.includesAny(
                2,
                "equals",
                "does-not-equal",
                "contains",
                "does-not-contain",
                "starts-with",
                "ends-with",
              ),
            )
            .withPlaceholder("Search or enter value")
            .allowCustomValue(),
        ),
    )
    // Account group path
    .path("account-group-name", (path) =>
      path
        .column(pathOperators(OPERATORS.EQUALITY))
        .column(
          column
            .search("Select Account group", accountGroupItems)
            .withPlaceholder("Search")
            .allowCustomValue(),
        ),
    )
    .build();

  const handleSearch = useDebouncedCallback(
    (searchTerm: string, columnIndex: number, selections?: string[]) => {
      const value = searchTerm.trim();
      const activeCriteria = selections?.[0];

      startTransition(() => {
        // Determine which fragment to refetch based on active criteria and column
        if (activeCriteria === "account-name") {
          // columnIndex includes criteria column: 0=criteria, 1=operator, 2=account value
          if (columnIndex === 2) {
            const searchFilter = value
              ? { single: { name: "name", value } }
              : null;
            refetchAccounts({
              filter: combineFilters(searchFilter),
            });
          }
        } else if (activeCriteria === "account-tag") {
          // columnIndex includes criteria column: 0=criteria, 1=tag key, 2=operator, 3=tag value
          if (columnIndex === 1) {
            // Tag keys are not provider-scoped; search without provider filter
            const searchFilter = value
              ? { single: { name: "key", value } }
              : null;
            refetchTagKeys({
              filter: searchFilter,
            });
          } else if (columnIndex === 3) {
            // Tag values - always pass value (even empty string) to maintain tag key context
            refetchTagValues({ filter: { single: { name: "value", value } } });
          }
        } else if (activeCriteria === "account-group-name") {
          // columnIndex includes criteria column: 0=criteria, 1=operator, 2=account group value
          if (columnIndex === 2) {
            const searchFilter = value
              ? { single: { name: "name", value } }
              : null;
            refetchAccountGroups({
              filter: combineFilters(searchFilter),
            });
          }
        }
      });
    },
    300,
  );

  const handleScrollEnd = (columnIndex: number, selections?: string[]) => {
    const activeCriteria = selections?.[0];

    if (activeCriteria === "account-name") {
      // columnIndex includes criteria column: 0=criteria, 1=operator, 2=account value
      if (columnIndex === 2 && hasNextAccounts) {
        loadNextAccounts(100);
      }
    } else if (activeCriteria === "account-tag") {
      // columnIndex includes criteria column: 0=criteria, 1=tag key, 2=operator, 3=tag value
      if (columnIndex === 1 && hasNextTagKeys) {
        loadNextTagKeys(100);
      } else if (columnIndex === 3 && hasNextTagValues) {
        loadNextTagValues(100);
      }
    } else if (activeCriteria === "account-group-name") {
      // columnIndex includes criteria column: 0=criteria, 1=operator, 2=account group value
      if (columnIndex === 2 && hasNextAccountGroups) {
        loadNextAccountGroups(100);
      }
    }
  };

  return (
    <FilterCriteriaCard
      helperTextOptions={["Account Name", "Account Tag", "Account Group"]}
      onRemoveCriterion={handleRemove}
      selectedCriteria={criteriaFilters}
      title="Account"
    >
      <SteppedMultiColumnDropdown
        config={config}
        initialSelections={initialSelections}
        onChangeStructured={handleChange}
        onScrollEnd={handleScrollEnd}
        onSearch={handleSearch}
      />
    </FilterCriteriaCard>
  );
}
